import { useCallback } from 'react';
import { useCanvasStores, useCanvasRotation, useCanvasLayers } from '../stores/CanvasContext';
import { GRID_SIZE, screenToCanvas as screenToCanvasUtil, generateTextBlockId, generateTextLabelId, generateGroupId, generateSpriteId, generateImageId, generateBlockNodeId, generateShapeId, generateAdrTriggerId, DEFAULT_TEXTBLOCK_WIDTH, DEFAULT_TEXTBLOCK_HEIGHT } from '../types';
import { NODE_WIDTH, NODE_HEIGHT, DEFAULT_SPRITE_SIZE, DEFAULT_IMAGE_WIDTH, DEFAULT_BLOCKNODE_WIDTH, DEFAULT_BLOCKNODE_HEIGHT, DEFAULT_SHAPE_WIDTH, DEFAULT_SHAPE_HEIGHT, ADR_TRIGGER_SIZE } from '../entityTypes';
import type { TaskData, TextBlockData, TextLabelData, GroupData, ImageData, BlockNodeData, ShapeData, AdrTriggerData } from '../entityTypes';
import type { SpriteCatalogEntry } from '../sprites/spriteTypes';
import type { SpriteData } from '../sprites/spriteTypes';

const DEFAULT_SPRITE_CIRCLE: SpriteData = {
  spriteId: 'custom:circle-shape',
  library: 'custom',
  label: 'Circle',
  svgContent: '<circle cx="32" cy="32" r="26" fill="none" stroke="currentColor" stroke-width="2.5"/>',
  viewBox: '0 0 64 64',
  fillMode: 'stroke',
  color: '#374151',
  strokeWidth: 2,
};

function buildSpriteData(catalogEntry?: SpriteCatalogEntry): SpriteData {
  if (!catalogEntry) return { ...DEFAULT_SPRITE_CIRCLE };
  return {
    spriteId: catalogEntry.spriteId,
    library: catalogEntry.library,
    label: catalogEntry.label,
    svgContent: catalogEntry.svgContent,
    viewBox: catalogEntry.viewBox,
    fillMode: catalogEntry.fillMode,
    color: '#374151',
    strokeWidth: 2,
  };
}

export function useCanvasActions() {
  const { store, panZoomStore, containerRef } = useCanvasStores();
  const rotation = useCanvasRotation();
  const layers = useCanvasLayers();

  const getCanvasCenter = useCallback(() => {
    const container = containerRef.current;
    if (!container) return { x: 100, y: 100 };
    const rect = container.getBoundingClientRect();
    const pz = panZoomStore.getSnapshot().context;
    return screenToCanvasUtil(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
      container, pz, rotation,
    );
  }, [panZoomStore, containerRef, rotation]);

  const getCanvasPosition = useCallback((screenPos?: { x: number; y: number }) => {
    if (screenPos) {
      const container = containerRef.current;
      if (container) {
        const pz = panZoomStore.getSnapshot().context;
        return screenToCanvasUtil(screenPos.x, screenPos.y, container, pz, rotation);
      }
    }
    return getCanvasCenter();
  }, [getCanvasCenter, panZoomStore, containerRef, rotation]);

  const getActiveLayerId = useCallback(() => {
    return store.getSnapshot().context.activeLayerId ?? layers[0]?.id ?? '';
  }, [store, layers]);

  const resolveLayerId = useCallback((layerId?: string) => {
    return layerId ?? getActiveLayerId();
  }, [getActiveLayerId]);

  const handleAddTask = useCallback((taskId: string, layerId?: string, screenPos?: { x: number; y: number }) => {
    const center = getCanvasPosition(screenPos);
    const x = Math.round((center.x - 100) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round((center.y - 30) / GRID_SIZE) * GRID_SIZE;
    store.send({ type: 'entity.add', entity: {
      id: taskId, kind: 'task', x, y, width: NODE_WIDTH, height: NODE_HEIGHT,
      layerId: resolveLayerId(layerId), connectable: true,
      zIndex: 0, data: { taskId } satisfies TaskData,
    } });
  }, [store, getCanvasPosition, resolveLayerId]);

  const handleAddTextBlock = useCallback((layerId?: string, screenPos?: { x: number; y: number }) => {
    const center = getCanvasPosition(screenPos);
    const x = Math.round((center.x - DEFAULT_TEXTBLOCK_WIDTH / 2) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round((center.y - DEFAULT_TEXTBLOCK_HEIGHT / 2) / GRID_SIZE) * GRID_SIZE;
    store.send({
      type: 'entity.add',
      entity: {
        id: generateTextBlockId(),
        kind: 'textblock',
        x, y,
        width: DEFAULT_TEXTBLOCK_WIDTH,
        height: DEFAULT_TEXTBLOCK_HEIGHT,
        layerId: resolveLayerId(layerId),
        connectable: true,
        zIndex: 0,
        data: {
          text: 'Text',
          backgroundColor: '#f3f4f6',
          textColor: '#1f2937',
          fontSize: 14,
          fontWeight: 'normal' as const,
          textAlign: 'left' as const,
        } satisfies TextBlockData,
      },
    });
  }, [store, getCanvasPosition, resolveLayerId]);

  const handleAddTextLabel = useCallback((layerId?: string, screenPos?: { x: number; y: number }) => {
    const center = getCanvasPosition(screenPos);
    const x = Math.round(center.x / GRID_SIZE) * GRID_SIZE;
    const y = Math.round(center.y / GRID_SIZE) * GRID_SIZE;
    store.send({
      type: 'entity.add',
      entity: {
        id: generateTextLabelId(),
        kind: 'textlabel',
        x, y,
        width: 0, height: 0,
        layerId: resolveLayerId(layerId),
        connectable: false,
        zIndex: 0,
        data: {
          text: 'Label',
          fontSize: 14,
          fontWeight: 'normal' as const,
          textColor: '#374151',
        } satisfies TextLabelData,
      },
    });
  }, [store, getCanvasPosition, resolveLayerId]);

  const handleAddGroup = useCallback((layerId?: string) => {
    const groupId = generateGroupId();
    // Read group count from store snapshot at event time (avoids subscribing to materialized)
    const rawGroups = Object.values(store.getSnapshot().context.groups);
    const groupCount = rawGroups.length + 1;
    store.send({
      type: 'entity.add',
      entity: {
        id: groupId,
        kind: 'group',
        x: 0, y: 0, width: 0, height: 0,
        layerId: resolveLayerId(layerId),
        connectable: false,
        zIndex: 0,
        data: {
          label: `Group ${groupCount}`,
          backgroundColor: 'rgba(147, 197, 253, 0.2)',
          collapsed: false,
          memberEntityIds: [],
        } satisfies GroupData,
      },
    });
  }, [store, resolveLayerId]);

  const handleAddSprite = useCallback((catalogEntry?: SpriteCatalogEntry, layerId?: string, screenPos?: { x: number; y: number }) => {
    const center = getCanvasPosition(screenPos);
    const size = DEFAULT_SPRITE_SIZE;
    const x = Math.round((center.x - size / 2) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round((center.y - size / 2) / GRID_SIZE) * GRID_SIZE;

    store.send({
      type: 'entity.add',
      entity: {
        id: generateSpriteId(),
        kind: 'sprite',
        x, y,
        width: size,
        height: size,
        layerId: resolveLayerId(layerId),
        connectable: true,
        zIndex: 0,
        data: buildSpriteData(catalogEntry) as unknown as Record<string, unknown>,
      },
    });
  }, [store, getCanvasPosition, resolveLayerId]);

  /** Add sprite at a specific screen position (for drag-and-drop from panel). */
  const handleAddSpriteAtScreen = useCallback((catalogEntry: SpriteCatalogEntry, screenX: number, screenY: number) => {
    const container = containerRef.current;
    if (!container) return;
    const pz = panZoomStore.getSnapshot().context;
    const canvasPos = screenToCanvasUtil(screenX, screenY, container, pz, rotation);
    const size = DEFAULT_SPRITE_SIZE;
    const x = Math.round((canvasPos.x - size / 2) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round((canvasPos.y - size / 2) / GRID_SIZE) * GRID_SIZE;

    store.send({
      type: 'entity.add',
      entity: {
        id: generateSpriteId(),
        kind: 'sprite',
        x, y,
        width: size,
        height: size,
        layerId: resolveLayerId(),
        connectable: true,
        zIndex: 0,
        data: buildSpriteData(catalogEntry) as unknown as Record<string, unknown>,
      },
    });
  }, [store, panZoomStore, containerRef, rotation, resolveLayerId]);

  const handleAddImage = useCallback((dataUrl: string, naturalWidth: number, naturalHeight: number, layerId?: string, screenPos?: { x: number; y: number }) => {
    const center = getCanvasPosition(screenPos);
    const displayWidth = Math.min(naturalWidth, DEFAULT_IMAGE_WIDTH);
    const displayHeight = Math.round(displayWidth * (naturalHeight / naturalWidth));
    const x = Math.round((center.x - displayWidth / 2) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round((center.y - displayHeight / 2) / GRID_SIZE) * GRID_SIZE;

    store.send({
      type: 'entity.add',
      entity: {
        id: generateImageId(),
        kind: 'image',
        x, y,
        width: displayWidth,
        height: displayHeight,
        layerId: resolveLayerId(layerId),
        connectable: true,
        zIndex: 0,
        data: {
          src: dataUrl,
          naturalWidth,
          naturalHeight,
          borderRadius: 0,
          opacity: 1,
        } satisfies ImageData as unknown as Record<string, unknown>,
      },
    });
  }, [store, getCanvasPosition, resolveLayerId]);

  const handleAddImageViaDialog = useCallback((layerId?: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const img = new window.Image();
        img.onload = () => {
          handleAddImage(dataUrl, img.naturalWidth, img.naturalHeight, layerId);
        };
        img.src = dataUrl;
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [handleAddImage]);

  const handleAddBlockNode = useCallback((layerId?: string, screenPos?: { x: number; y: number }) => {
    const center = getCanvasPosition(screenPos);
    const x = Math.round((center.x - DEFAULT_BLOCKNODE_WIDTH / 2) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round((center.y - DEFAULT_BLOCKNODE_HEIGHT / 2) / GRID_SIZE) * GRID_SIZE;
    store.send({
      type: 'entity.add',
      entity: {
        id: generateBlockNodeId(),
        kind: 'blocknode',
        x, y,
        width: DEFAULT_BLOCKNODE_WIDTH,
        height: DEFAULT_BLOCKNODE_HEIGHT,
        layerId: resolveLayerId(layerId),
        connectable: true,
        zIndex: 0,
        data: {
          content: [{ type: 'paragraph', content: [] }],
          backgroundColor: '#f3f4f6',
        } satisfies BlockNodeData as unknown as Record<string, unknown>,
      },
    });
  }, [store, getCanvasPosition, resolveLayerId]);

  const handleAddShape = useCallback((layerId?: string, screenPos?: { x: number; y: number }, shapeType?: ShapeData['shapeType']) => {
    const center = getCanvasPosition(screenPos);
    const x = Math.round((center.x - DEFAULT_SHAPE_WIDTH / 2) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round((center.y - DEFAULT_SHAPE_HEIGHT / 2) / GRID_SIZE) * GRID_SIZE;
    store.send({
      type: 'entity.add',
      entity: {
        id: generateShapeId(),
        kind: 'shape',
        x, y,
        width: DEFAULT_SHAPE_WIDTH,
        height: DEFAULT_SHAPE_HEIGHT,
        layerId: resolveLayerId(layerId),
        connectable: true,
        zIndex: 0,
        data: {
          shapeType: shapeType ?? 'rect',
          backgroundColor: '#dbeafe',
        } satisfies ShapeData as unknown as Record<string, unknown>,
      },
    });
  }, [store, getCanvasPosition, resolveLayerId]);

  const handleAddAdrTrigger = useCallback((adrIds: string[], layerId?: string, screenPos?: { x: number; y: number }) => {
    const center = getCanvasPosition(screenPos);
    const x = Math.round((center.x - ADR_TRIGGER_SIZE / 2) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round((center.y - ADR_TRIGGER_SIZE / 2) / GRID_SIZE) * GRID_SIZE;
    store.send({
      type: 'entity.add',
      entity: {
        id: generateAdrTriggerId(),
        kind: 'adr-trigger',
        x, y,
        width: ADR_TRIGGER_SIZE,
        height: ADR_TRIGGER_SIZE,
        layerId: resolveLayerId(layerId),
        connectable: false,
        zIndex: 0,
        data: { adrIds } satisfies AdrTriggerData as unknown as Record<string, unknown>,
      },
    });
  }, [store, getCanvasPosition, resolveLayerId]);

  const handleAttachResource = useCallback((entityId: string, resourceId: string) => {
    const entity = store.getSnapshot().context.nodes[entityId];
    if (!entity) return;
    const existing: string[] = (entity.data as any).attachedResourceIds ?? [];
    if (existing.includes(resourceId)) return;
    store.send({
      type: 'entity.setAttachedResourceIds', entityId,
      attachedResourceIds: [...existing, resourceId],
    });
  }, [store]);

  const handleDetachResource = useCallback((entityId: string, resourceId: string) => {
    const entity = store.getSnapshot().context.nodes[entityId];
    if (!entity) return;
    const existing: string[] = (entity.data as any).attachedResourceIds ?? [];
    store.send({
      type: 'entity.setAttachedResourceIds', entityId,
      attachedResourceIds: existing.filter(id => id !== resourceId),
    });
  }, [store]);

  /** Dispatch add by entity kind. For 'task', pass taskId. Returns false if kind is unknown. */
  const handleAddByKind = useCallback((kind: string, layerId?: string, taskId?: string, screenPos?: { x: number; y: number }) => {
    switch (kind) {
      case 'task':
        if (taskId) handleAddTask(taskId, layerId, screenPos);
        return;
      case 'textblock':
        handleAddTextBlock(layerId, screenPos);
        return;
      case 'textlabel':
        handleAddTextLabel(layerId, screenPos);
        return;
      case 'group':
        handleAddGroup(layerId);
        return;
      case 'sprite':
        handleAddSprite(undefined, layerId, screenPos);
        return;
      case 'image':
        handleAddImageViaDialog(layerId);
        return;
      case 'blocknode':
        handleAddBlockNode(layerId, screenPos);
        return;
      case 'shape':
        handleAddShape(layerId, screenPos);
        return;
    }
  }, [handleAddTask, handleAddTextBlock, handleAddTextLabel, handleAddGroup, handleAddSprite, handleAddImageViaDialog, handleAddBlockNode, handleAddShape]);

  const handleRotate = useCallback(() => {
    store.send({ type: 'canvas.setRotation', rotation: (rotation + 90) % 360 });
  }, [store, rotation]);

  return { handleAddTask, handleAddTextBlock, handleAddTextLabel, handleAddGroup, handleAddSprite, handleAddSpriteAtScreen, handleAddImage, handleAddImageViaDialog, handleAddBlockNode, handleAddShape, handleAddAdrTrigger, handleAttachResource, handleDetachResource, handleAddByKind, handleRotate };
}
